/**
 * Mirror of backend serviceDuration for enrollment start/end preview in the UI.
 *
 * service_duration + unit  → allotment / rate multi (e.g. 600 minute)
 * duration_period + unit   → calendar validity (e.g. 1 month, 15 day)
 */

export function normalizeDurationUnit(unit) {
  return String(unit || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_');
}

export function toYmd(dateLike) {
  if (dateLike == null || dateLike === '') return null;
  if (dateLike instanceof Date) {
    if (Number.isNaN(dateLike.getTime())) return null;
    return dateLike.toISOString().slice(0, 10);
  }
  const s = String(dateLike).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
}

export function isCalendarDurationUnit(unit) {
  const u = normalizeDurationUnit(unit);
  return (
    u === 'day' ||
    u === 'days' ||
    u === 'week' ||
    u === 'weeks' ||
    u === 'month' ||
    u === 'months' ||
    u === 'year' ||
    u === 'years'
  );
}

export function calculateEnrollmentEndDate(startsAt, durationAmount, durationUnit) {
  const start = toYmd(startsAt);
  if (!start) return null;
  const n = Number(durationAmount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const unit = normalizeDurationUnit(durationUnit);
  const d = new Date(`${start}T12:00:00.000Z`);
  if (Number.isNaN(d.getTime())) return null;

  switch (unit) {
    case 'minute':
    case 'min':
    case 'minutes':
      d.setUTCMinutes(d.getUTCMinutes() + n);
      break;
    case 'hour':
    case 'hours':
    case 'hr':
    case 'hrs':
      d.setUTCHours(d.getUTCHours() + n);
      break;
    case 'day':
    case 'days':
      d.setUTCDate(d.getUTCDate() + n);
      break;
    case 'week':
    case 'weeks':
      d.setUTCDate(d.getUTCDate() + n * 7);
      break;
    case 'month':
    case 'months':
      d.setUTCMonth(d.getUTCMonth() + n);
      break;
    case 'year':
    case 'years':
      d.setUTCFullYear(d.getUTCFullYear() + n);
      break;
    default:
      return null;
  }
  return d.toISOString().slice(0, 10);
}

/**
 * Prefer package duration_period (validity window); fall back to service_duration
 * when that unit is calendar-based (legacy packages).
 */
export function calculatePackageEnrollmentEndDate(startsAt, pkg = {}) {
  const periodEnd = calculateEnrollmentEndDate(
    startsAt,
    pkg.duration_period,
    pkg.duration_period_unit
  );
  if (periodEnd) return periodEnd;

  if (isCalendarDurationUnit(pkg.service_duration_unit)) {
    return calculateEnrollmentEndDate(
      startsAt,
      pkg.service_duration,
      pkg.service_duration_unit
    );
  }
  return null;
}

export function formatDurationLabel(amount, unit) {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return null;
  const u = normalizeDurationUnit(unit) || 'unit';
  return `${n} ${u}`;
}

/** e.g. "600 minute / 1 month" or "120 min for 15 day" */
export function formatPackageDurationLabel(pkg = {}) {
  const allot = formatDurationLabel(pkg.service_duration, pkg.service_duration_unit);
  const period = formatDurationLabel(pkg.duration_period, pkg.duration_period_unit);
  if (allot && period) return `${allot} / ${period}`;
  if (period) return `valid ${period}`;
  if (allot) return allot;
  return null;
}
